import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import LessonViewer from './components/LessonViewer';
import { freeLessons, FreeLesson } from './data/freeLessonsData';
import SEOHead from '../../components/SEOHead';

const gradeFilters = ['All', 'Elementary', 'Middle School', 'High School', 'Test Prep'];

export default function FreeLessonsPage() {
  const navigate = useNavigate();
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Free Mathematics Lessons — K-12 Math by Augustine Anyimadu-Ahenkae',
    description:
      'Free interactive math lessons for every grade level from K-12. Full concept explanations, instant-feedback practice questions, and expert tips from NYC teacher Augustine Anyimadu-Ahenkae.',
    url: `${siteUrl}/free-lessons`,
    provider: {
      '@type': 'Person',
      name: 'Augustine Anyimadu-Ahenkae',
      url: siteUrl,
    },
    educationalLevel: 'K-12',
    inLanguage: 'en',
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Fractions (Grades 3-5)', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Intro to Algebra (Grades 6-8)', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Geometry Basics (Grades 7-9)', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Linear Equations (Grades 8-9)', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Quadratic Equations (Grades 9-10)', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'SAT Math Strategies (Grades 10-12)', courseMode: 'online' },
    ],
  };

  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedLesson, setSelectedLesson] = useState<FreeLesson | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filtered =
    activeFilter === 'All'
      ? freeLessons
      : freeLessons.filter((l) => l.gradeLevel === activeFilter);

  const difficultyColor = (d: string) =>
    d === 'Beginner'
      ? 'bg-green-100 text-green-700'
      : d === 'Intermediate'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-rose-100 text-rose-700';

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      await fetch('https://readdy.ai/api/form/d6oan4t39lnhn4hh17gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Free Math Lessons K-12 New York | Augustine Anyimadu-Ahenkae | No Sign-Up Required"
        description="Access free interactive mathematics lessons for every grade (K-12). Fractions, algebra, geometry, quadratics, and SAT strategies. Full explanations, practice questions, and teacher tips — 100% free, no credit card needed."
        canonical="/free-lessons"
        keywords="free math lessons, K-12 mathematics, free algebra lessons, free geometry lessons, SAT math practice, free online math tutoring"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 bg-gradient-to-br from-teal-50 via-cyan-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100/40 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-100/30 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors cursor-pointer">Home</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-teal-600 font-semibold">Free Lessons</span>
          </div>

          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-5 py-2 rounded-full text-sm font-bold mb-5">
                <i className="ri-gift-line text-lg"></i>
                No Credit Card Required — 100% Free
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-gray-900 leading-tight mb-5">
                Free Math Lessons<br />
                <span className="text-teal-600">For Every Grade</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mb-8">
                Experience Mr. Augustine's teaching style with fully interactive lessons. Read the concept, study the key points, then test yourself with instant-feedback practice questions — completely free.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-book-open-line', label: 'Full Concept Explanations' },
                  { icon: 'ri-question-answer-line', label: 'Instant Answer Feedback' },
                  { icon: 'ri-star-line', label: "Teacher's Tips Included" },
                  { icon: 'ri-trophy-line', label: 'Score Tracking' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-teal-200 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm">
                    <i className={`${b.icon} text-teal-500`}></i> {b.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Stats card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 w-full lg:w-72 flex-shrink-0">
              <h3 className="text-base font-black text-gray-900 mb-5">What's Included — Free</h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-book-2-line', label: '6 Full Lessons', sub: 'Grades 3–12 & Test Prep' },
                  { icon: 'ri-question-line', label: '60 Practice Questions', sub: '10 per lesson with solutions' },
                  { icon: 'ri-lightbulb-line', label: 'Key Concepts', sub: 'Step-by-step explanations' },
                  { icon: 'ri-star-line', label: "Teacher's Tips", sub: 'From Mr. Augustine' },
                  { icon: 'ri-infinity-line', label: 'Unlimited Retakes', sub: 'Practice as many times as you like' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-teal-600 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/#membership-tiers')}
                className="mt-6 w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                Unlock Full Access
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <span className="text-sm font-semibold text-gray-500 whitespace-nowrap mr-2">Filter by level:</span>
            {gradeFilters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === f
                    ? 'bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lessons Grid */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <h2 className="text-3xl font-black text-gray-900 mb-2">
              {activeFilter === 'All' ? 'All Free Lessons' : `${activeFilter} Lessons`}
            </h2>
            <p className="text-gray-500 text-base">
              {filtered.length} lesson{filtered.length !== 1 ? 's' : ''} available — click any card to start learning
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((lesson) => (
              <div
                key={lesson.id}
                onClick={() => setSelectedLesson(lesson)}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-teal-200 transition-all duration-300 group cursor-pointer overflow-hidden"
              >
                {/* Card top */}
                <div className={`${lesson.bgColor} px-7 py-6`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl shadow-sm">
                      <i className={`${lesson.icon} text-2xl ${lesson.color}`}></i>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${difficultyColor(lesson.difficulty)}`}>
                      {lesson.difficulty}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1 group-hover:text-teal-700 transition-colors">
                    {lesson.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{lesson.description}</p>
                </div>

                {/* Card bottom */}
                <div className="px-7 py-5">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-graduation-cap-line text-teal-500"></i>
                      <span className="font-semibold">{lesson.grade}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-teal-500"></i>
                      <span>{lesson.duration}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-question-line text-teal-500"></i>
                      <span>{lesson.questions.length} questions</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-teal-600 font-bold text-sm group-hover:gap-3 transition-all duration-300">
                    <i className="ri-play-circle-line text-lg"></i>
                    <span>Start Free Lesson</span>
                    <i className="ri-arrow-right-line ml-auto"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Unlock more CTA */}
          <div className="mt-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-3xl p-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-white shadow-xl">
            <div>
              <h3 className="text-3xl font-black mb-2">Want More? Unlock the Full Course Library</h3>
              <p className="text-teal-100 text-base max-w-xl leading-relaxed">
                Get unlimited access to 1,500+ video lessons, 3,000+ worksheets, full Regents exam prep, AP Calculus, and direct Q&amp;A support from Mr. Augustine.
              </p>
            </div>
            <button
              onClick={() => navigate('/#membership-tiers')}
              className="px-10 py-4 bg-white text-teal-700 font-black rounded-2xl hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap text-base shadow-lg flex-shrink-0"
            >
              View Membership Plans
            </button>
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-black text-gray-900 mb-4">Topics Covered in Free Lessons</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Each free lesson covers a core math topic with full explanations, key vocabulary, and practice questions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-pie-chart-2-line', color: 'text-orange-500', bg: 'bg-orange-50', title: 'Fractions (Grades 3–5)', points: ['Numerator & denominator', 'Comparing fractions', 'Adding & subtracting fractions', 'Real-world fraction problems'] },
              { icon: 'ri-function-line', color: 'text-teal-500', bg: 'bg-teal-50', title: 'Intro to Algebra (Grades 6–8)', points: ['Variables & expressions', 'Writing equations', 'Solving one-step equations', 'Evaluating expressions'] },
              { icon: 'ri-shape-line', color: 'text-emerald-500', bg: 'bg-emerald-50', title: 'Geometry Basics (Grades 7–9)', points: ['Angles & triangles', 'Pythagorean Theorem', 'Area of shapes', 'Circle formulas'] },
              { icon: 'ri-line-chart-line', color: 'text-rose-500', bg: 'bg-rose-50', title: 'Linear Equations (Grades 8–9)', points: ['Slope-intercept form', 'Calculating slope', 'Graphing lines', 'Parallel & perpendicular lines'] },
              { icon: 'ri-bar-chart-grouped-line', color: 'text-violet-500', bg: 'bg-violet-50', title: 'Quadratic Equations (Grades 9–10)', points: ['Factoring quadratics', 'Quadratic formula', 'Discriminant analysis', 'Vertex & axis of symmetry'] },
              { icon: 'ri-medal-line', color: 'text-amber-500', bg: 'bg-amber-50', title: 'SAT Math Strategies (Grades 10–12)', points: ['Plug-in strategy', 'Systems of equations', 'Percent change', 'Function notation'] },
            ].map((topic, i) => (
              <div key={i} className={`${topic.bg} rounded-2xl p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl shadow-sm">
                    <i className={`${topic.icon} text-xl ${topic.color}`}></i>
                  </div>
                  <h3 className="font-black text-gray-900 text-base">{topic.title}</h3>
                </div>
                <ul className="space-y-2">
                  {topic.points.map((p, pi) => (
                    <li key={pi} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0"></i>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Mr. Augustine */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl p-10 flex flex-col md:flex-row items-center gap-10">
            <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-teal-200 flex-shrink-0 shadow-lg">
              <img
                src="https://public.readdy.ai/ai/img_res/7ed708d0-0e46-4404-b490-9c89f3f516ab.png"
                alt="Augustine Anyimadu-Ahenkae"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-sm font-bold text-teal-600 uppercase tracking-widest mb-2">Your Teacher</p>
              <h3 className="text-3xl font-black text-gray-900 mb-1">Augustine Anyimadu-Ahenkae</h3>
              <p className="text-gray-500 text-sm mb-4">NYC Public School Mathematics Teacher · 30+ Years Experience · Chancellor's Honor Award Recipient</p>
              <p className="text-gray-700 leading-relaxed text-base">
                "I have spent over 30 years helping students discover that math is not something to fear — it is something to master. These free lessons are my gift to every student who wants to build a strong foundation. Start here, and I promise you will see the difference."
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                {['95% Student Improvement Rate', 'Chancellor\'s Honor Award', '30+ Years Teaching', 'NYC Public Schools'].map((badge, i) => (
                  <span key={i} className="bg-teal-50 text-teal-700 text-xs font-bold px-4 py-2 rounded-full border border-teal-200">
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <i className="ri-mail-send-line text-5xl text-teal-500 mb-5 block"></i>
          <h2 className="text-3xl font-black text-gray-900 mb-3">Get New Free Lessons by Email</h2>
          <p className="text-gray-500 text-base mb-8">
            We add new free lessons regularly. Enter your email and we will notify you when new content is available — no spam, ever.
          </p>
          {submitted ? (
            <div className="bg-teal-50 border border-teal-200 rounded-2xl px-8 py-6">
              <i className="ri-checkbox-circle-fill text-teal-500 text-4xl mb-3 block"></i>
              <p className="text-teal-800 font-bold text-lg">You're on the list!</p>
              <p className="text-teal-600 text-sm mt-1">We'll notify you when new free lessons are added.</p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-5 py-3.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400 transition-colors"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Notify Me'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-600 to-cyan-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-black mb-4">Ready to Go Beyond Free?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Unlock the complete K–12 math curriculum — every grade, every topic, every Regents exam, AP Calculus, and personal tutoring support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/#membership-tiers')}
              className="px-10 py-4 bg-white text-teal-700 font-black rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg shadow-lg"
            >
              Get Full Access
            </button>
            <button
              onClick={() => navigate('/grades')}
              className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap text-lg"
            >
              Browse All Grades
            </button>
          </div>
          <p className="mt-5 text-teal-200 text-sm">One-time payment · Lifetime access · No recurring fees</p>
        </div>
      </section>

      <Footer />

      {/* Lesson Viewer Modal */}
      {selectedLesson && (
        <LessonViewer lesson={selectedLesson} onClose={() => setSelectedLesson(null)} />
      )}
    </div>
  );
}
